import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch } from 'react-icons/fa';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';

import { useDebounce } from 'src/hooks';
import type { ActionItemStatus } from 'src/types/followUp';
import { apiPaths } from 'src/services/api/apiPaths';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import OpenPopupProvider from 'src/components/app/context/OpenPopupContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import Accordion, {
  AccordionItem,
  AccordionItemHeader,
  AccordionItemPanel,
} from 'src/components/common/Accordion';
import DateFilterControl from 'src/components/followUp/FilterColumn/DateFilter/DateFilterControl';
import type { All } from 'src/components/followUp/FilterColumn/FilterColumn';
import FilterSection from 'src/components/followUp/FilterColumn/FilterSection';
import FollowUpOwnerSearch from 'src/components/followUp/FilterColumn/FollowUpOwnerSearch';
import useOwnerListQuery from 'src/components/myFollowUp/hooks/useOwnerListQuery';
import useFetchSurveyList from 'src/components/survey/SurveyContext/ListContext/useFetchSurveyList';

import { styles } from './index.styles';

const MyFollowUpSidebar: FC = () => {
  const { t } = useTranslation('followUp');
  const { user } = useUserContext();

  // Search
  const { setFilter } = useFilterContext();
  const [searchString, setSearchString] = useState<string>('');
  const debouncedSearchString = useDebounce(searchString, 500);
  useEffect(() => {
    setFilter(
      'search',
      debouncedSearchString
        ? [
            {
              label: debouncedSearchString,
              filterExpr: { value: debouncedSearchString },
            },
          ]
        : [],
    );
  }, [debouncedSearchString]);

  // Survey filter
  const { data: surveys } = useFetchSurveyList();

  // Follow-Up Owner filter
  const [ownerSearchString, setOwnerSearchString] = useState<string>('');
  const debouncedOwnerSearch = useDebounce(ownerSearchString, 500);
  const { data: followUpOwners } = useOwnerListQuery({
    path: apiPaths.MY_FOLLOW_UP.OWNERS,
    search: debouncedOwnerSearch,
  });

  // Status filter
  const ticketStatus: Array<{
    value: ActionItemStatus | typeof All;
    translationKey: string;
  }> = [
    { value: 'Open', translationKey: 'status.open' },
    { value: 'Closed', translationKey: 'status.closed' },
    { value: 'In Progress', translationKey: 'status.inProgress' },
    { value: 'Overdue', translationKey: 'status.overdue' },
  ];

  return (
    <OpenPopupProvider>
      <Accordion initiallyActiveItems={['table-filter-accordion-item']}>
        <AccordionItem itemName="table-filter-accordion-item">
          <AccordionItemHeader headline={t('filter.ticketFilter')} />
          <AccordionItemPanel>
            <Box sx={styles.filterColumn}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: '600', mb: 2 }}>
                  {t('myFollowUp:filter.timePeriod')}
                </Typography>
                <DateFilterControl />
              </Box>

              <TextField
                aria-label="Action search"
                fullWidth
                placeholder={t('toolBar.searchActions')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch />
                    </InputAdornment>
                  ),
                  sx: (theme) => ({
                    border: '2px solid',
                    borderColor: theme.colors.neutral[100],
                  }),
                }}
                value={searchString}
                onChange={(event) => setSearchString(event.target.value)}
              />

              <FilterSection
                filterKey={'survey'}
                allFilterOptions={
                  surveys?.map(({ id, name }) => ({
                    value: String(id),
                    displayString: name,
                  })) || []
                }
                popupKey="survey"
              >
                {t('toolBar.surveyFilterLabel')}
              </FilterSection>

              <FilterSection
                filterKey={'followUpOwner'}
                allFilterOptions={[
                  {
                    value: user ? String(user.id) : '',
                    displayString: t('myTickets'),
                  },
                  ...(followUpOwners?.map(({ id, fullName }) => ({
                    value: String(id),
                    displayString: fullName,
                  })) ?? []),
                ]}
                popupKey="owner"
                searchBar={
                  <FollowUpOwnerSearch
                    searchString={ownerSearchString}
                    setSearchString={setOwnerSearchString}
                  />
                }
              >
                {t('toolBar.followUpOwnerFilterLabel')}
              </FilterSection>

              <FilterSection
                filterKey={'status'}
                allFilterOptions={ticketStatus.map(
                  ({ value, translationKey }) => ({
                    value,
                    displayString: t(translationKey),
                  }),
                )}
                popupKey="status"
              >
                {t('toolBar.ticketStatusFilterLabel')}
              </FilterSection>
            </Box>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
    </OpenPopupProvider>
  );
};

export default MyFollowUpSidebar;
